import type { BiKeywordSovCategory, BiKeywordSovChoice, BiKeywordSovRow } from "@/resources/brand-intelligence";

export const ALL_CATEGORIES = "all";
// Pseudo-value of the niche view: the chips' `value` while `choice` holds several leaves. Never a chip option.
export const NICHE = "niche";

export interface PickedCategoryFilter {
  ids: string[];
  byChip: boolean;
}

export interface KeywordSovViewState {
  chip: string;
  categoryFilter: string[];
}

// The view and the `Category/ Niche` filter, derived from the stored choice and what the user picked.
export function deriveKeywordSovView(
  choice: BiKeywordSovChoice | null,
  categories: BiKeywordSovCategory[],
  pickedChip: string | null,
  pickedFilter: PickedCategoryFilter | null,
): KeywordSovViewState {
  const defaultView = choice
    ? choice.leaf_category_ids.length === 1
      ? choice.leaf_category_ids[0]
      : NICHE
    : categories[0]?.leaf_category_id ?? ALL_CATEGORIES;
  const followsDefault = !(
    pickedChip === ALL_CATEGORIES ||
    (pickedChip !== null && categories.some((c) => c.leaf_category_id === pickedChip))
  );
  const chip = followsDefault ? defaultView : (pickedChip as string);
  const categoryFilter =
    pickedFilter && !(pickedFilter.byChip && followsDefault)
      ? pickedFilter.ids
      : followsDefault && choice
        ? choice.leaf_category_ids
        : [];
  return { chip, categoryFilter };
}

// Keywords sitting in any of the niche's leaves; a keyword in two of them appears once.
export function rowsForNiche(keywords: BiKeywordSovRow[], leafIds: string[]): BiKeywordSovRow[] {
  const wanted = leafIds.map(String);
  return keywords.filter((r) => r.leaf_category_ids.some((id) => wanted.includes(String(id))));
}

export interface KeywordSovRowFilters {
  search: string;
  competitors: string[];
  categories: string[];
}

export interface KeywordSovFooter {
  count: number;
  monthlyVolume: number;
  kwRevenue: number | null;
  sovByBrand: Record<string, number | null>;
}

// Half away from zero, like PHP round($x, 2). The toPrecision step drops binary
// representation noise so 1.005 -> 1.01 (Math.round(1.005 * 100) / 100 gives 1).
export function round2(x: number): number {
  if (x === 0) return 0;
  const scaled = Number((Math.abs(x) * 100).toPrecision(15));
  return (Math.sign(x) * Math.round(scaled)) / 100;
}

export function shareOf(row: BiKeywordSovRow, brand: string): number | null {
  const hit = row.brand_breakdown.find((b) => b.brand === brand);
  return hit ? hit.sov_pct : null;
}

export function rowsForChip(keywords: BiKeywordSovRow[], chip: string): BiKeywordSovRow[] {
  if (chip === ALL_CATEGORIES) return keywords;
  return keywords.filter((r) => r.leaf_category_ids.map(String).includes(String(chip)));
}

export function applyRowFilters(rows: BiKeywordSovRow[], filters: KeywordSovRowFilters): BiKeywordSovRow[] {
  const needle = filters.search.trim().toLowerCase();
  const cats = filters.categories.map(String);
  return rows.filter((r) => {
    if (needle && !r.keyword.toLowerCase().includes(needle)) return false;
    if (filters.competitors.length > 0 && !filters.competitors.every((c) => (shareOf(r, c) ?? 0) > 0)) return false;
    if (cats.length > 0 && !r.leaf_category_ids.some((id) => cats.includes(String(id)))) return false;
    return true;
  });
}

export function footerTotals(rows: BiKeywordSovRow[], brands: string[]): KeywordSovFooter {
  const monthlyVolume = rows.reduce((s, r) => s + r.monthly_volume, 0);
  const kwRevenue = rows.every((r) => r.kw_revenue === null)
    ? null
    : rows.reduce((s, r) => s + (r.kw_revenue ?? 0), 0);

  const sovByBrand: Record<string, number | null> = {};
  for (const brand of brands) {
    let weighted = 0;
    let anyShare = false;
    for (const r of rows) {
      const share = shareOf(r, brand);
      if (share !== null) {
        anyShare = true;
        weighted += share * r.monthly_volume;
      }
    }
    sovByBrand[brand] = !anyShare || monthlyVolume === 0 ? null : round2(weighted / monthlyVolume);
  }
  return { count: rows.length, monthlyVolume, kwRevenue, sovByBrand };
}
